/**
 * Clipboard helpers shared by direct message-copy actions and copy actions
 * whose text first has to be fetched from the backend.
 */

/** Copy known text, with a legacy fallback for older WebViews. */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to execCommand. Some embedded/PWA browsers expose the
    // Clipboard API but reject it even during a user gesture.
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.readOnly = true;
  textarea.setAttribute("aria-hidden", "true");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, textarea.value.length);

  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}

/**
 * Start a clipboard write synchronously from the user's click while the text
 * is still being fetched. ClipboardItem accepts a promised Blob, which keeps
 * the browser's transient user activation attached to the original action.
 *
 * Returns null when this browser lacks the promised-ClipboardItem path; the
 * caller can then await its text and use copyTextToClipboard as a fallback.
 */
export function copyPromisedTextToClipboard(
  textPromise: Promise<string>,
): Promise<boolean> | null {
  if (!navigator.clipboard?.write || typeof globalThis.ClipboardItem !== "function") {
    return null;
  }

  try {
    const textBlob = textPromise.then(
      (text) => new Blob([text], { type: "text/plain" }),
    );
    const item = new ClipboardItem({ "text/plain": textBlob });
    return navigator.clipboard.write([item]).then(
      () => true,
      () => false,
    );
  } catch {
    return Promise.resolve(false);
  }
}
